import type {
  ApiDirectPurchaseList,
  DirectPurchasePayload,
  DirectPurchaseResponse,
} from 'src/types/directPurchase';

import { useQuery, useMutation } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';
import { LIVE_STALE_TIME } from './staleness';
import { useRequestSession } from './useRequestSession';

// Records a Buy Now (direct) purchase. The client only sends the on-chain txHash and
// the listing id — the backend derives seller/amount/currency from the listing and the
// buyer from the account's wallet. The purchase is created as `pending`; the seller
// is notified only after the background worker verifies the transfer on-chain.
export const useDirectPurchase = () =>
  useMutation({
    mutationFn: async (payload: DirectPurchasePayload) => {
      const response = await axios.post<DirectPurchaseResponse>('/buyer/direct-purchase', payload);

      return response.data;
    },
  });

// The buyer's own direct (Buy Now) purchases, each with a listing snapshot. Mirrors
// the seller feed; the endpoint returns the full set `{ items, total }` (no pagination),
// so a plain useQuery is enough (like useListRentals).
//
// Half of the Purchases tab, read under the account session, which the api scopes to the
// account's primary wallet (app#238). Buying itself still needs the wallet — that is a transfer,
// not a read. Keyed by the session it is read under, like the delivery half (app#248).
export const useListBuyerDirectPurchases = (options?: { enabled?: boolean }) => {
  const session = useRequestSession();

  return useQuery({
    queryKey: QueryKeys.forSession(QueryKeys.directPurchases(), session),
    queryFn: async (): Promise<ApiDirectPurchaseList> => {
      const response = await axios.get<ApiDirectPurchaseList>('/buyer/direct-purchase');

      return response.data;
    },
    staleTime: LIVE_STALE_TIME,
    enabled: options?.enabled ?? true,
  });
};

// Polls the verification status of an own purchase (owner-only endpoint). Polling
// stops on a terminal status; the server guarantees one within ~15 minutes (a tx
// never seen on-chain is failed with a timeout reason).
export const useDirectPurchaseStatus = (id: string | null) => {
  const session = useRequestSession();

  return useQuery({
    queryKey: QueryKeys.forSession(QueryKeys.directPurchase(id ?? ''), session),
    queryFn: async () => {
      const response = await axios.get<DirectPurchaseResponse>(`/buyer/direct-purchase/${id}`);

      return response.data;
    },
    enabled: !!id,
    refetchInterval: (query) => (query.state.data?.status === 'pending' ? 5000 : false),
  });
};
